import { Box } from "@mui/material";

import {
  getEnumLabel,
  getModuleStatusOptions,
  getModuleTypeOptions,
  getTextLlmOptions,
  getVoiceLlmOptions,
} from "Forms/Utils/EnumOptions";
import type { ModuleDetailDto } from "Infrastructure/Api/Api";
import { Resources, useResource } from "Infrastructure/Translations/Resources";
import { AppRouting } from "Infrastructure/Utils/UrlUtils";
import { EntityLink } from "Shared/EntityLink";
import {
  type InformationSection,
  InformationView,
} from "Shared/InformationView";

interface ModuleInformationProps {
  module: ModuleDetailDto;
  priceCategoryName?: string;
  priceCategoryID?: string;
  onEdit?: () => void;
}

export const ModuleInformation = ({
  module,
  priceCategoryName,
  priceCategoryID,
  onEdit,
}: ModuleInformationProps): React.ReactNode => {
  const { t } = useResource();

  const sections: InformationSection[] = [
    {
      items: [
        {
          label: t(Resources.Modules.ModuleDetail.ModuleName),
          value: module.name,
        },
        {
          label: t(Resources.Modules.ModuleDetail.ModuleType),
          value: getEnumLabel(getModuleTypeOptions(), module.type),
        },
        {
          label: t(Resources.Modules.ModuleDetail.Status),
          value: getEnumLabel(getModuleStatusOptions(), module.status),
        },
        {
          label: t(Resources.Modules.ModuleDetail.PriceCategory),
          value:
            priceCategoryName && priceCategoryID ? (
              <EntityLink
                label={priceCategoryName}
                path={AppRouting.PriceCategoryDetail.path(priceCategoryID)}
              />
            ) : (
              (priceCategoryName ?? "-")
            ),
        },
        {
          label: t(Resources.Modules.ModuleDetail.TextModels),
          value:
            module.textLlms
              .map((v) => getEnumLabel(getTextLlmOptions(), v))
              .join(", ") || "-",
        },
        {
          label: t(Resources.Modules.ModuleDetail.VoiceModels),
          value:
            module.voiceLlms
              .map((v) => getEnumLabel(getVoiceLlmOptions(), v))
              .join(", ") || "-",
        },
        {
          label: t(Resources.Modules.ModuleDetail.CustomPromptSection),
          value: (
            <Box
              sx={{
                maxHeight: "400px",
                overflow: "auto",
                pr: 2,
                pt: 2,
                fontSize: "12px",
                "& p": { margin: 0 },
                "& h1, & h2, & h3": { margin: 0 },
              }}
              // biome-ignore lint/security/noDangerouslySetInnerHtml: Content is sanitized on backend
              dangerouslySetInnerHTML={{ __html: module.customPrompt }}
            />
          ),
          size: 12,
        },
      ],
    },
  ];

  return (
    <InformationView
      title={t(Resources.Modules.ModuleDetail.Information)}
      editLabel={t(Resources.Modules.ModuleDetail.EditInformation)}
      onEdit={onEdit}
      sections={sections}
    />
  );
};
